import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type * as ImageStackTypes from '../../../../WasmModules/ImageStack';
import * as THREE from 'three';
import type { IImageSliceRenderingContext } from './IImageSliceRenderingContext';
import type { WasmBuffer } from '@/engine/Utilities/WasmBuffer';

export class ImageSlice implements IDisposable {
  private readonly _wrapped: ImageStackTypes.ImageSlice;

  private readonly _size: THREE.Vector2;

  private readonly _sliceCS: THREE.Matrix4;

  public constructor(toWrap: ImageStackTypes.ImageSlice) {
    this._wrapped = toWrap;
    this._size = new THREE.Vector2().fromArray(this._wrapped.getSize());
    this._sliceCS = new THREE.Matrix4().fromArray(this._wrapped.getSliceCS());
  }

  public dispose(): void {
    this._wrapped.dispose();
  }

  // ------------------ Wrapped methods -------------------------

  public getSliceIndex(): number {
    return this._wrapped.getSliceIndex();
  }

  public getSlicePosition(): number {
    return this._wrapped.getSlicePosition();
  }

  public getSliceCS(): THREE.Matrix4 {
    return this._sliceCS;
  }

  public render(
    renderingContext: IImageSliceRenderingContext,
    contrastWindowFrom: { min: number; max: number },
    contrastWindowTo = { min: 0, max: 255 }
  ): void {
    if (this._size.x != renderingContext.width || this._size.y != renderingContext.height) {
      throw new Error('Invalid rendering context size');
    }
    renderingContext.render((buffer: WasmBuffer): void => {
      const imageView = this._wrapped.calculateView(
        contrastWindowFrom.min,
        contrastWindowFrom.max,
        contrastWindowTo.min,
        contrastWindowTo.max
      );
      const size = imageView.getSize();
      if (size[0] * size[1] * 4 != buffer.size) {
        throw new Error('Invalid rendering buffer size');
      }
      imageView.fillRGBAPixelBuffer(buffer.ptr, buffer.size);
      imageView.dispose();
    });
  }
}
